import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Text, Textarea } from '@mantine/core';
import {type DiscoveryStore, useDiscoveryStore} from '../../../stores/useDiscoveryStore';
import { IconArrowUp, IconCornerDownRight } from '@tabler/icons';
import { getPresetsForCategory } from '../discoveryPromptPresets';
import {
  sendDiscoveryMessageWithRecovery,
  createDiscoverySession,
  DISCOVERY_SESSION_USER_MESSAGE_LIMIT_MESSAGE,
  type Artifact,
  type DiscoverySessionMessage,
  type SendDiscoveryMessageResponse,
} from '../../../api/discoveryAgent';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { discoveryKeys } from '../../../api/discoveryAgent';
import { useDiscoveryChat, useActiveDiscoverySession, patchDiscoverySessionLimitInCache } from '../../../hooks/useDiscoveryAgent';
import { useDiscoverySessionUrl } from '../../../hooks/useDiscoverySessionUrl';
import { useAbortOnUnmount } from '../../../hooks/useAbortOnUnmount';
import { useIdeaFilters } from '../../../hooks/useIdeaFilters';
import { buildSessionFilters, getCustomerParentKeysFromConfig } from '../utils/buildSessionFilters';
import ExploreFilter from './ExploreFilter';
import ExploreDiscoveryFilters from './ExploreDiscoveryFilters';
import {usePrompts} from "../../../hooks/useAiAgent";
import _ from "lodash";
import { CreateIdeaInputPrompt } from '../../../pages/ai-agent/createIdea/CreateIdeaInputPrompt';
import type { CreateIdeaOption } from '../../../pages/ai-agent/createIdea/createIdeaFromChatPrompt';

/** Pre-fill React Query so the thread does not flash empty when sessionId appears before GET completes. */
function seedDiscoveryMessagesCache(
  queryClient: QueryClient,
  sessionId: string,
  userText: string,
  assistantText: string | undefined,
  userId: string,
  artifacts?: Artifact[]
) {
  const now = new Date().toISOString();
  const messages: DiscoverySessionMessage[] = [
    {
      id: `seed-user-${now}`,
      sessionId,
      userId,
      messageType: 'user',
      content: userText,
      timestamp: now,
    },
  ];
  if (assistantText?.trim()) {
    const agentMessage: DiscoverySessionMessage = {
      id: `seed-agent-${now}`,
      sessionId,
      userId: '',
      messageType: 'agent',
      content: assistantText.trim(),
      timestamp: now,
    };
    if (artifacts?.length) {
      agentMessage.metadata = { artifacts };
    }
    messages.push(agentMessage);
  }
  queryClient.setQueryData(discoveryKeys.messages(sessionId), {
    pages: [{ success: true, messages, hasMore: false }],
    pageParams: [undefined],
  });
}

const EXPLORE_NEW_CHAT_PLACEHOLDER = 'Ask about themes, impact, trends, or specific pain points...';

interface ExploreChatProps {
  isNewChat?: boolean;
  /** Create-idea inline options-list mode — only used on the active-session composer */
  createIdeaActive?: boolean;
  createIdeaOptions?: CreateIdeaOption[];
  onCreateIdeaConfirm?: (option: CreateIdeaOption) => void;
  onCreateIdeaDismiss?: () => void;
  /**
   * True while the create-idea modal's background request is in flight.
   * Blocks the composer send so no user message interleaves between the
   * hidden create-idea prompt and its agent reply (which are stored adjacent).
   */
  blockComposerSend?: boolean;
  /** Called when the chat API returns a suggested_idea artifact — avoids Zustand round-trip */
  onSuggestedIdeaReceived?: (artifact: { title: string; description: string }) => void;
}

export const ExploreChat = ({
  isNewChat = false,
  createIdeaActive = false,
  createIdeaOptions = [],
  onCreateIdeaConfirm,
  onCreateIdeaDismiss,
  blockComposerSend = false,
  onSuggestedIdeaReceived,
}: ExploreChatProps) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const queryClient = useQueryClient();
  const { sessionId, setSessionId } = useDiscoverySessionUrl();
  const runUntilUnmount = useAbortOnUnmount();
  const {prompts} = usePrompts('discovery');
  const effectiveSessionId = sessionId ?? '';
  const inputValue = useDiscoveryStore((s) => s.inputValue);
  const isMessageSending = useDiscoveryStore((s: DiscoveryStore) => s.isMessageSending);
  const messageSendingSessionId = useDiscoveryStore((s: DiscoveryStore) => s.messageSendingSessionId);
  const startMessageSend = useDiscoveryStore((s) => s.startMessageSend);
  const stopMessageSend = useDiscoveryStore((s) => s.stopMessageSend);
  const setIsNewSession = useDiscoveryStore((s) => s.setIsNewSession);
  const setOptimisticUserMessage = useDiscoveryStore((s) => s.setOptimisticUserMessage);
  const setPendingSessionId = useDiscoveryStore((s) => s.setPendingSessionId);
  const setInputValue = useDiscoveryStore((s) => s.setInputValue);
  const discoveryPromptCategoryKey = useDiscoveryStore((s) => s.discoveryPromptCategoryKey);
  const discoveryPromptCategoryHeading = useDiscoveryStore((s) => s.discoveryPromptCategoryHeading);
  const setDiscoveryPromptCategory = useDiscoveryStore((s) => s.setDiscoveryPromptCategory);
  const updateSendingSessionId = useDiscoveryStore((s) => s.updateSendingSessionId);
  const { ideasFiltersConfig } = useIdeaFilters();
  const customerParentKeys = getCustomerParentKeysFromConfig(ideasFiltersConfig ?? {});

  const { sendMessage, isSending: isChatSending } = useDiscoveryChat(effectiveSessionId || '', { onSuggestedIdeaReceived });
  const { session: activeSession } = useActiveDiscoverySession();
  const isUserMessageLimitReached = activeSession?.isUserMessageLimitReached === true;
  // isChatSending covers existing sessions (mutation.isPending).
  // isMessageSending covers the new-chat path where there is no session ID yet.
  const isComposerSending =
    isChatSending ||
    (isMessageSending &&
      (messageSendingSessionId === effectiveSessionId ||
        (isNewChat && messageSendingSessionId === '' && !effectiveSessionId)));
  const placeholder = isNewChat
    ? (discoveryPromptCategoryHeading?.trim() || EXPLORE_NEW_CHAT_PLACEHOLDER)
    : 'Ask a follow up';

  const categoryPromptList = useMemo(
    () => (discoveryPromptCategoryKey ? getPresetsForCategory(discoveryPromptCategoryKey, prompts) : []),
    [discoveryPromptCategoryKey, prompts]
  );
  const categoryPrefixLabel = discoveryPromptCategoryKey
    ? _.capitalize(discoveryPromptCategoryKey)
    : '';

  useEffect(() => {
    if (!isNewChat) {
      setDiscoveryPromptCategory(null, null);
    }
  }, [isNewChat, setDiscoveryPromptCategory]);

  const showCategoryPromptPicker =
    isNewChat && Boolean(discoveryPromptCategoryKey) && categoryPromptList.length > 0;

  const dismissCategoryPromptPickerIfOpen = () => {
    if (discoveryPromptCategoryKey) {
      setDiscoveryPromptCategory(null, null);
    }
  };

  const composerSurfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showCategoryPromptPicker) return;
    const onPointerDown = (e: MouseEvent) => {
      const root = composerSurfaceRef.current;
      if (!root) return;
      if (!root.contains(e.target as Node)) {
        setDiscoveryPromptCategory(null, null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [showCategoryPromptPicker, setDiscoveryPromptCategory]);

  const applyNewSessionChatResult = (
    sid: string,
    text: string,
    chatResult: SendDiscoveryMessageResponse
  ) => {
    if (chatResult.isUserMessageLimitReached) {
      patchDiscoverySessionLimitInCache(queryClient, sid, chatResult);
      queryClient.invalidateQueries({ queryKey: [...discoveryKeys.sessions(), 'byId', sid] });
    }

    const suggestedArtifact =
      chatResult.artifacts?.[0]?.type === 'suggested_idea' ? chatResult.artifacts[0] : undefined;

    if (suggestedArtifact) {
      const responseText = chatResult.response?.trim() ?? '';
      if (responseText) {
        queryClient.setQueryData(discoveryKeys.messages(sid), (old: any) => {
          if (!old?.pages) return old;
          let latestTs = -Infinity;
          let latestIdx: { page: number; msg: number } | null = null;
          old.pages.forEach((page: any, pi: number) => {
            (page.messages ?? []).forEach((m: any, mi: number) => {
              if (m.messageType === 'agent' && m.content?.trim() === responseText) {
                const ts = new Date(m.timestamp).getTime();
                if (ts > latestTs) {
                  latestTs = ts;
                  latestIdx = { page: pi, msg: mi };
                }
              }
            });
          });
          if (!latestIdx) return old;
          return {
            ...old,
            pages: old.pages.map((page: any, pi: number) => ({
              ...page,
              messages: (page.messages ?? []).map((m: any, mi: number) =>
                pi === latestIdx!.page && mi === latestIdx!.msg ? { ...m, content: '' } : m
              ),
            })),
          };
        });
      } else {
        queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sid) });
      }
      onSuggestedIdeaReceived?.({
        title: suggestedArtifact.title ?? '',
        description: suggestedArtifact.description ?? '',
      });
    } else if (chatResult.response?.trim()) {
      seedDiscoveryMessagesCache(
        queryClient,
        sid,
        text,
        chatResult.response,
        user?.sub ?? '',
        chatResult.artifacts
      );
    } else {
      queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sid) });
    }
    queryClient.invalidateQueries({ queryKey: discoveryKeys.sessions() });
  };

  const createAndSendNewSession = async (text: string, parentSessionId?: string) => {
    startMessageSend(effectiveSessionId || null);
    setOptimisticUserMessage(text);
    setInputValue('');
    setIsNewSession(true);

    const storeFilters = useDiscoveryStore.getState().sessionFilters;
    const uiFilters = buildSessionFilters(storeFilters, customerParentKeys);

    let sid: string | undefined;
    try {
      const sessionResult = await createDiscoverySession(
        { getAccessTokenSilently },
        uiFilters,
        parentSessionId
      );
      sid = sessionResult.sessionId;
    } catch {
      setInputValue(text);
      setOptimisticUserMessage(null);
      setIsNewSession(false);
      stopMessageSend();
      return;
    }

    if (!sid) {
      setInputValue(text);
      setOptimisticUserMessage(null);
      setIsNewSession(false);
      stopMessageSend();
      return;
    }

    setPendingSessionId(sid);
    seedDiscoveryMessagesCache(queryClient, sid, text, undefined, user?.sub ?? '');
    setSessionId(sid);
    updateSendingSessionId(sid);
    queryClient.invalidateQueries({ queryKey: discoveryKeys.sessions() });

    runUntilUnmount((signal) =>
      sendDiscoveryMessageWithRecovery(sid, text, { getAccessTokenSilently }, uiFilters, { signal })
    )
      .then((chatResult) => {
        applyNewSessionChatResult(sid!, text, chatResult);
      })
      .catch(() => {
        queryClient.invalidateQueries({ queryKey: discoveryKeys.messages(sid!) });
      })
      .finally(() => {
        stopMessageSend();
      });
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isComposerSending || blockComposerSend) return;
    const currentSessionId = effectiveSessionId;
    setDiscoveryPromptCategory(null, null);

    if (!currentSessionId || isUserMessageLimitReached) {
      const parentSessionId =
        isUserMessageLimitReached && currentSessionId
          ? activeSession?.id ?? currentSessionId
          : undefined;
      await createAndSendNewSession(text, parentSessionId);
      return;
    }

    setInputValue('');
    startMessageSend(currentSessionId);
    try {
      await sendMessage(text);
    } finally {
      stopMessageSend();
    }
  };

  // ── Create-idea inline prompt mode ────────────────────────────────────────
  // When active, replace the entire composer with the inline prompt widget.
  if (createIdeaActive && createIdeaOptions.length > 0 && onCreateIdeaConfirm && onCreateIdeaDismiss) {
    return (
      <Box id="explore-discovery-composer" sx={{ width: '100%', position: 'relative' }}>
        <CreateIdeaInputPrompt
          options={createIdeaOptions}
          onConfirm={onCreateIdeaConfirm}
          onDismiss={onCreateIdeaDismiss}
          disabled={isComposerSending}
        />
      </Box>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box id="explore-discovery-composer" sx={{ width: '100%', position: 'relative' }}>
      {isUserMessageLimitReached && (
        <Text
          size="sm"
          c="dimmed"
          sx={{ marginBottom: 8, lineHeight: 1.5, textAlign: 'center' }}
        >
          {DISCOVERY_SESSION_USER_MESSAGE_LIMIT_MESSAGE}
        </Text>
      )}
      <Box
        ref={composerSurfaceRef}
        sx={{
          borderRadius: 20,
          padding: '2px',
          background: isNewChat
            ? 'linear-gradient(88.7deg, rgba(92, 92, 235, 0.15) 9.98%, rgba(255, 135, 0, 0.8) 99.83%)'
            : 'transparent',
          boxShadow: '0px 2px 12px 2px #0000000D',
          border: isNewChat ? 'none' : '1px solid #D8D8DB',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 18,
            backgroundColor: '#FFFFFF',
          }}
        >
          <Textarea
            placeholder={placeholder}
            value={inputValue}
            onFocus={dismissCategoryPromptPickerIfOpen}
            onClick={dismissCategoryPromptPickerIfOpen}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setInputValue(v);
              if (discoveryPromptCategoryKey && v.length > 0) {
                setDiscoveryPromptCategory(null, null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            autosize
            minRows={1}
            maxRows={12}
            sx={{
              '& .mantine-Textarea-wrapper, & .mantine-Input-wrapper': {
                overflow: 'hidden',
              },
              '& .mantine-Textarea-input': {
                borderRadius: '18px 18px 0 0',
                color: '#131416',
                border: 'none',
                padding: '16px 16px 8px 16px',
                fontSize: 15,
                lineHeight: 1.5,
                backgroundColor: '#FFFFFF',
                resize: 'none',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  width: 0,
                  height: 0,
                  display: 'none',
                },
                '&:focus': {
                  border: 'none',
                  outline: 'none',
                },
              },
            }}
          />
          {showCategoryPromptPicker && (
            <Box
              sx={{
                padding: '0 16px 12px',
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {categoryPromptList.map((preset) => (
                  <Box
                    key={preset.id}
                    component="button"
                    type="button"
                    onClick={() => {
                      setInputValue(preset.prompt);
                      setDiscoveryPromptCategory(null, null);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      width: '100%',
                      padding: '8px 4px',
                      border: 'none',
                      borderRadius: 8,
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: '#212529',
                      '&:hover': {
                        backgroundColor: '#F4F5F8',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        marginTop: 2,
                        color: '#212529',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconCornerDownRight size={16} stroke={1.5} />
                    </Box>
                    <Text color='#7b808d' size="sm" sx={{ lineHeight: 1.45 }}>
                      <Text component="span" color='#212529' fw={700}>
                        {categoryPrefixLabel}:
                      </Text>{' '}
                      {preset.prompt}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent:
                isNewChat && !showCategoryPromptPicker ? 'space-between' : 'flex-end',
              alignItems: 'center',
              padding: '8px 16px 15px',
              gap: 8,
            }}
          >
            {!showCategoryPromptPicker && (
              <Box sx={{ display: 'flex', flex: 1, justifyContent: 'flex-start', gap: 5 }}>
                <ExploreFilter key='productArea' filterType={'productArea'} isNewChat={isNewChat}/>
                {isNewChat && <ExploreDiscoveryFilters /> }
              </Box>
            )}
            <Button
              size="sm"
              onClick={handleSend}
              loading={isComposerSending}
              disabled={!inputValue.trim() || blockComposerSend}
              leftIcon={
                !isComposerSending && isUserMessageLimitReached
                  ? <IconArrowUp size={16} stroke={2} color="#FFFFFF" />
                  : undefined
              }
              sx={{
                flexShrink: 0,
                cursor: 'pointer',
                ...(isUserMessageLimitReached
                  ? {
                    height: 30,
                    padding: '0 10px',
                    borderRadius: 4,
                  }
                  : {
                    width: 30,
                    height: 30,
                    padding: 0,
                    borderRadius: 4,
                  }),
                backgroundColor: '#FF8700',
                color: '#FFFFFF',
                border: 'none',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: '#FF8700',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
                },
                '&:disabled': {
                  backgroundColor: '#ffedd9',
                  color: '#FFFFFF',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                  cursor: 'not-allowed',
                },
                '& .mantine-Button-inner': {
                  justifyContent: 'center',
                },
                '& .mantine-Button-icon': {
                  justifyContent: 'center',
                  marginRight: isUserMessageLimitReached ? 4 : 0,
                },
                '& .mantine-Button-label': {
                  fontSize: 13,
                  fontWeight: 600,
                },
              }}
            >
              {isUserMessageLimitReached && !isComposerSending ? 'New session' : null}
              {!isUserMessageLimitReached && !isComposerSending && (
                <IconArrowUp size={20} stroke={2} color="#FFFFFF" />
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ExploreChat;